import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  checkResetPasswordTokenAPI,
  createTotpAPI,
  getClientTokenAPI,
  login,
  recoverEmail,
  resetPasswordAPI,
  ResetPasswordRequest,
  Token,
  TokenRecovery,
  verifyTotpAPI,
} from "./loginAPI";
import localforage, { clear } from "localforage";
import jwt_decode from "jwt-decode";
import { LoadingStatus } from "../common/commonSlice";
import {
  ClientBrandResponse,
  getClientBrandByCodeAPI,
} from "../common/commonApi";

export interface TokenJson {
  clientId: string;
  metaclientId: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  upn: string;
  userId: string;
  username: string;
}

// Tipos del estado del login
export interface LoginState {
  username: string;
  password: string;
  email: string;
  loading: boolean;
  loadingRecovery: LoadingStatus;
  loadingResetPassword: LoadingStatus;
  errorMessage: string;
  tokenString: string;
  emailRecovery: string;
  sentEmailRecovery: boolean;
  resetPasswordError: string;
  tokenJson: TokenJson;
  clientBrand: ClientBrandResponse | null;
  loadingClientBrand: LoadingStatus;
  needsToCreateOtp: boolean;
  createOtpToken?: string;
  needsToValidateOtp: boolean;
  validateOtpToken?: string;
  totpSecret?: string;
  totpUri?: string;
  totpErrorMessage?: string;
}

// Estado inicial del login
const initialState: LoginState = {
  username: "",
  password: "",
  email: "",
  loading: false,
  errorMessage: "",
  tokenString: "",
  emailRecovery: "",
  sentEmailRecovery: false,
  loadingRecovery: "idle",
  loadingResetPassword: "idle",
  resetPasswordError: "",
  tokenJson: {
    clientId: "",
    metaclientId: "",
    exp: 0,
    iat: 0,
    iss: "",
    jti: "",
    upn: "",
    userId: "",
    username: "",
  },
  clientBrand: null,
  loadingClientBrand: "idle",
  needsToCreateOtp: false,
  needsToValidateOtp: false,
};

// Thunks que permite realizar funciones asincronicas y poder definir que
// pasa segun el resultado (rechazado, aceptado, pendiente).
export const tryLogin = createAsyncThunk(
  "login/tryLogin",
  async (_, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const username = currentState.login.username;
    const password = currentState.login.password;

    const token = await login(username, password);
    if (!token.loggedIn) {
      return rejectWithValue(token);
    } else {
      await localforage.setItem("token", token.token);
      return token;
    }
  }
);

export const getClientToken = createAsyncThunk(
  "login/getClientToken",
  async (clientId: string, { rejectWithValue }) => {
    const token = await getClientTokenAPI(clientId);
    if (!token) {
      return rejectWithValue(token);
    } else {
      await localforage.setItem("token", token.token);
      return token;
    }
  }
);

export const recovery = createAsyncThunk(
  "login/recovery",
  async (_, { getState }) => {
    const currentState = getState() as RootState;
    const email = currentState.login.emailRecovery;

    const token = await recoverEmail(email);
    return token;
  }
);

export const checkResetPasswordToken = createAsyncThunk(
  "login/checkResetPasswordToken",
  async (token: string, { rejectWithValue }) => {
    const checkResponse = await checkResetPasswordTokenAPI(token);
    if (!checkResponse.valid) {
      return rejectWithValue(checkResponse);
    } else {
      return checkResponse;
    }
  }
);

export const resetPassword = createAsyncThunk(
  "login/resetPassword",
  async (req: ResetPasswordRequest, { rejectWithValue }) => {
    const response = await resetPasswordAPI(req.token, req.newPassword);
    if (!response.valid) {
      return rejectWithValue(response);
    } else {
      return response;
    }
  }
);

export const getClientBrandByCode = createAsyncThunk(
  "common/getClientBrandByCode",
  async (code: string, { rejectWithValue }) => {
    const clientBrand = await getClientBrandByCodeAPI(code);
    if (clientBrand.error) {
      return rejectWithValue(clientBrand.error);
    }
    if (!clientBrand.data) {
      return rejectWithValue(clientBrand.error);
    } else {
      return clientBrand.data;
    }
  }
);

export const generateTotp = createAsyncThunk(
  "login/generateTotp",
  async (_, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const token = currentState.login.createOtpToken;
    if (!token) {
      return rejectWithValue("No se encontró el token");
    }
    // Llamar a la API para generar el TOTP
    try {
      let response = await createTotpAPI(token);
      return response;
    } catch (err: any) {
      console.error(err);
      return rejectWithValue("Hubo un error generando el TOTP");
    }
  }
);

export const verifyTotp = createAsyncThunk(
  "login/verifyTotp",
  async (code: string, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const token = currentState.login.validateOtpToken;
    if (!token) {
      return rejectWithValue("No se encontró el token");
    }
    // Llamar a la API para verificar el TOTP
    try {
      let response = await verifyTotpAPI({ code: code }, token);
      await localforage.setItem("token", response.token);
      return response;
    } catch (err: any) {
      console.error(err);
      return rejectWithValue("Hubo un error verificando el TOTP");
    }
  }
);

// Slice, contiene los reducers que se ejecutan sincronicamente
// tambien extraReducers que contienen casos segun la respuesta de los thunk que se
// ejecutan asincronicamente
export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    changeUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    changePassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    changeEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    emptyTokenString: (state) => {
      state.tokenString = "";
    },
    emptyErrorMessage: (state) => {
      state.errorMessage = "";
    },
    changeEmailRecovery: (state, action: PayloadAction<string>) => {
      state.emailRecovery = action.payload;
    },
    changeSentEmailRecovery: (state, action: PayloadAction<boolean>) => {
      state.sentEmailRecovery = action.payload;
    },
    setLoadingClientBrand: (state, action: PayloadAction<LoadingStatus>) => {
      state.loadingClientBrand = action.payload;
      if (action.payload === "rejected") {
        state.clientBrand = null;
      }
    },
    clearTotpErrorMessage: (state) => {
      state.totpErrorMessage = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(tryLogin.fulfilled, (state, action) => {
        let token = action.payload as Token;
        if (token) {
          if (token.redirectToTotpGeneration === true) {
            state.needsToCreateOtp = true;
            state.loading = false;
            state.createOtpToken = token.token;
          } else if (token.redirectToTotpValidation === true) {
            state.needsToValidateOtp = true;
            state.loading = false;
            state.validateOtpToken = token.token;
          } else if (token.loggedIn) {
            let decoded = jwt_decode(token.token) as TokenJson;
            state.username = "";
            state.password = "";
            state.tokenString = token.token;
            state.errorMessage = "";
            state.tokenJson = decoded;
          }
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.password = "";
        state.loading = false;
      })
      .addCase(tryLogin.rejected, (state, action) => {
        let token = action.payload as Token;
        if (token) {
          state.errorMessage = token.errorMessage;
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.password = "";
        state.loading = false;
      })
      .addCase(tryLogin.pending, (state) => {
        state.loading = true;
      })
      // Recovery Email
      .addCase(recovery.fulfilled, (state, action) => {
        let token = action.payload as TokenRecovery;
        state.emailRecovery = "";
        if (token) {
          state.sentEmailRecovery = true;
          state.errorMessage = "";
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.loading = false;
      })
      .addCase(recovery.rejected, (state, action) => {
        let token = action.payload as TokenRecovery;
        if (token) {
          state.errorMessage = token.errorMessage;
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.loading = false;
      })
      .addCase(recovery.pending, (state) => {
        state.loading = true;
      });
    // Check Reset Password Token
    builder
      .addCase(checkResetPasswordToken.fulfilled, (state, action) => {
        if (action.payload.valid) {
          state.loadingRecovery = "resolved";
        } else {
          state.loadingRecovery = "rejected";
        }
      })
      .addCase(checkResetPasswordToken.rejected, (state, action) => {
        let check = action.payload as { errorMessage: string; valid: boolean };
        if (check.valid) {
          state.loadingRecovery = "resolved";
        } else {
          state.loadingRecovery = "rejected";
        }
      })
      .addCase(checkResetPasswordToken.pending, (state) => {
        state.loadingRecovery = "pending";
      });
    // Reset Password
    builder
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.errorMessage = "";
        state.loadingResetPassword = "resolved";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        let check = action.payload as { errorMessage: string; valid: boolean };
        if (check.valid) {
          state.errorMessage = "";
          state.loadingResetPassword = "resolved";
        } else {
          state.errorMessage = check.errorMessage;
          state.loadingResetPassword = "rejected";
        }
      })
      .addCase(resetPassword.pending, (state) => {
        state.loadingResetPassword = "pending";
      });

    // SubClient Token
    builder
      .addCase(getClientToken.fulfilled, (state, action) => {
        let token = action.payload as Token;
        if (token) {
          if (token.loggedIn) {
            let decoded = jwt_decode(token.token) as TokenJson;
            state.username = "";
            state.password = "";
            state.tokenString = token.token;
            state.errorMessage = "";
            state.tokenJson = decoded;
          }
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.password = "";
        state.loading = false;
      })
      .addCase(getClientToken.rejected, (state, action) => {
        let token = action.payload as Token;
        if (token) {
          state.errorMessage = token.errorMessage;
        } else {
          state.errorMessage = "Hubo un error conectandote";
        }
        state.password = "";
        state.loading = false;
      })
      .addCase(getClientToken.pending, (state) => {
        state.loading = true;
      });

    //
    // GetClientBrandByCode
    builder
      .addCase(getClientBrandByCode.pending, (state) => {
        state.clientBrand = null;
        state.loadingClientBrand = "pending";
      })
      .addCase(getClientBrandByCode.fulfilled, (state, action) => {
        state.clientBrand = action.payload;
        state.loadingClientBrand = "resolved";
      })
      .addCase(getClientBrandByCode.rejected, (state) => {
        state.clientBrand = null;
        state.loadingClientBrand = "rejected";
      });
    // Generate TOTP
    builder
      .addCase(generateTotp.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
      })
      .addCase(generateTotp.fulfilled, (state, action) => {
        state.loading = false;
        let response = action.payload;
        if (response) {
          state.totpSecret = response.secret;
          state.totpUri = response.uri;
          state.createOtpToken = undefined;
          state.validateOtpToken = response.validateToken;
        }
        state.errorMessage = "";
      })
      .addCase(generateTotp.rejected, (state, action) => {
        state.loading = false;
        let error = action.payload as string;
        if (error) {
          state.totpErrorMessage = error;
        } else {
          state.totpErrorMessage = "Hubo un error generando el TOTP";
        }
      });
    // Verify TOTP
    builder
      .addCase(verifyTotp.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
      })
      .addCase(verifyTotp.fulfilled, (state, action) => {
        let token = action.payload;
        if (token) {
          if (token.loggedIn === true) {
            let decoded = jwt_decode(token.token) as TokenJson;
            state.username = "";
            state.password = "";
            state.tokenString = token.token;
            state.errorMessage = "";
            state.tokenJson = decoded;
            state.needsToValidateOtp = false;
            state.needsToCreateOtp = false;
            state.validateOtpToken = undefined;
          } else {
            state.totpErrorMessage = "El código no es válido";
          }
        } else {
          state.totpErrorMessage = "El código no es válido";
        }
        state.password = "";
        state.loading = false;
      })
      .addCase(verifyTotp.rejected, (state, action) => {
        let error = action.payload as string;
        if (error) {
          state.totpErrorMessage = error;
        } else {
          state.totpErrorMessage = "Hubo un error verificando el TOTP";
        }
        state.password = "";
        state.loading = false;
      });
  },
});

// Se exponen las acciones que se pueden realizar en este slice.
export const {
  changePassword,
  changeUsername,
  changeEmail,
  changeEmailRecovery,
  emptyTokenString,
  emptyErrorMessage,
  changeSentEmailRecovery,
  setLoadingClientBrand,
  clearTotpErrorMessage,
} = loginSlice.actions;

// Selectors: Se exportan los valores que contiene el slice.
export const selectUsername = (state: RootState) => state.login.username;
export const selectPassword = (state: RootState) => state.login.password;
export const selectEmail = (state: RootState) => state.login.email;
export const selectLoginLoading = (state: RootState) => state.login.loading;
export const selectLoginErrorMessage = (state: RootState) =>
  state.login.errorMessage;

export const selectTokenString = (state: RootState) => state.login.tokenString;
export const selectEmailRecovery = (state: RootState) =>
  state.login.emailRecovery;
export const selectSentEmailRecovery = (state: RootState) =>
  state.login.sentEmailRecovery;
export const selectLoadingRecovery = (state: RootState) =>
  state.login.loadingRecovery;
export const selectLoadingResetPassword = (state: RootState) =>
  state.login.loadingResetPassword;
export const selectResetPasswordError = (state: RootState) =>
  state.login.resetPasswordError;
export const selectTokenJson = (state: RootState) => state.login.tokenJson;
export const selectClientBrand = (state: RootState) => state.login.clientBrand;
export const selectLoadingClientBrand = (state: RootState) =>
  state.login.loadingClientBrand;
export const selectNeedsToCreateOtp = (state: RootState) =>
  state.login.needsToCreateOtp;
export const selectNeedsToValidateOtp = (state: RootState) =>
  state.login.needsToValidateOtp;
export const selectTotpSecret = (state: RootState) => state.login.totpSecret;
export const selectTotpUri = (state: RootState) => state.login.totpUri;
export const selectCreateTotpToken = (state: RootState) =>
  state.login.createOtpToken;
export const selectValidateTotpToken = (state: RootState) =>
  state.login.validateOtpToken;
export const selectTotpErrorMessage = (state: RootState) =>
  state.login.totpErrorMessage;
// Se exporta el reducer para poder ser utilizado en el store global
export default loginSlice.reducer;
